import styled from "@emotion/styled";
import { Slider as BaseSlider, sliderClasses } from "@mui/base/Slider";
import { Box, alpha } from "@mui/material";
import React from "react";

import { GridBigViewIcon, GridViewIcon, ListViewIcon } from "assets/icons";
import { Typography } from "new-components/typography/Typography";

const Slider = styled(BaseSlider)(
  () => `
    color: #C8CDD3;
    height: 6px;
    width: 100%;
    padding: 16px 0;
    display: inline-block;
    position: relative;
    cursor: pointer;
    touch-action: none;
    -webkit-tap-highlight-color: transparent;
  
    &.${sliderClasses.disabled} { 
      pointer-events: none;
      cursor: default;
      color: #C8CDD3;
      opacity: 0.5;
    }
  
    & .${sliderClasses.rail} {
      display: block;
      position: absolute;
      width: 100%;
      height: 4px;
      border-radius: 2px;
      background-color: #C8CDD3;
    }
  
    & .${sliderClasses.track} {
      display: block;
      position: absolute;
      height: 4px;
      border-radius: 2px;
      background-color: #C8CDD3;
    }
  
    & .${sliderClasses.thumb} {
      position: absolute;
      width: 7px;
      height: 17px;
      margin-left: -6px;
      margin-top: -6px;
      box-sizing: border-box;
      border-radius: 100px;
      outline: 0;
      background-color: #49494D;
      display: flex;
      flex-direction: column-reverse;
  
      &:hover{
        box-shadow: 0 0 0 4px ${alpha("#49494D", 0.3)};
      }
      
      &.${sliderClasses.focusVisible} {
        box-shadow: 0 0 0 4px #49494D;
        outline: none;
      }
  
      &.${sliderClasses.active} {
        box-shadow: 0 0 0 5px ${alpha("#49494D", 0.5)};
        outline: none;
      }
  
    }
  
    & .${sliderClasses.mark} {
      position: absolute;
      width: 8px;
      height: 8px;
      border-radius: 99%;
      background-color: #C8CDD3;
      top: 43%;
      transform: translateX(-50%);
      display: none;
    }
  
    & .${sliderClasses.markActive} {
      background-color: #C8CDD3;
    }
  `
);

export interface VisibilityActionsProps {
  gridView: boolean;
  setGridView: (objToUpdate: Record<string, string>) => void;
  gridSize: number;
  setGridSize: React.Dispatch<React.SetStateAction<number>>;
}

export function VisibilityActions(props: VisibilityActionsProps) {
  const { gridView, setGridView, gridSize, setGridSize } = props;

  const onChangeView = (isGrid: boolean) => {
    setGridView({
      grid_view: isGrid.toString(),
    });
  };

  const onChangeSize = (_: Event, newValue: number | number[]) => {
    setGridSize(newValue as number);
  };

  return (
    <Box className="flex gap-8 px-2 py-[9px]">
      {gridView && (
        <Box className="flex gap-2 items-center">
          <Typography textTransform="uppercase">
            Size:
          </Typography>
          <Box className="flex justify-between items-center gap-2 text-gray-500">
            <GridViewIcon color="#919599" />

            <Box sx={{ width: 100 }}>
              <Slider aria-label="Grid" value={gridSize} onChange={onChangeSize} step={1} marks min={1} max={4} />
            </Box>

            <GridBigViewIcon color="#919599" />
          </Box>
        </Box>
      )}

      <Box className="flex gap-2 items-center">
        <Typography textTransform="uppercase">
          View:
        </Typography>
        <Box className="flex gap-1">
          <Box
            onClick={() => onChangeView(false)}
            className={`p-1 ${
              gridView ? "bg-white" : "bg-[#919599]"
            } hover:bg-gray-300 hover:text-black transition-all rounded-lg cursor-pointer flex justify-center items-center`}
            data-testid="list-view-button"
          >
            <ListViewIcon color={gridView ? "#919599" : "#ffffff"} />
          </Box>

          <Box
            onClick={() => onChangeView(true)}
            className={`p-1 ${
              !gridView ? "bg-white" : "bg-[#919599]"
            } hover:bg-gray-300 hover:text-black transition-all rounded-lg cursor-pointer flex justify-center items-center`}
          >
            <GridViewIcon color={!gridView ? "#919599" : "#ffffff"} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
